/** @file Hallen-Modus: Fullscreen + high-contrast overlay with localStorage persistence. */

const STORAGE_KEY = "halle-mode";

/** @returns {boolean} */
export function isHallenMode() {
  return document.documentElement.classList.contains("hallen-mode");
}

/**
 * Enable hallen mode: set class + request fullscreen + persist.
 * Fullscreen failures (e.g. iOS Safari on iPad) are non-fatal.
 * @returns {Promise<void>}
 */
export async function enableHallenMode() {
  document.documentElement.classList.add("hallen-mode");
  try {
    const el = /** @type {any} */ (document.documentElement);
    if (typeof el.requestFullscreen === "function") {
      await el.requestFullscreen();
    } else if (typeof el.webkitRequestFullscreen === "function") {
      el.webkitRequestFullscreen();
    }
  } catch (err) {
    console.warn("[hallen-mode] fullscreen not available", err);
  }
  try {
    localStorage.setItem(STORAGE_KEY, "on");
  } catch {
    /* no-op (private browsing) */
  }
}

/**
 * @returns {Promise<void>}
 */
export async function disableHallenMode() {
  document.documentElement.classList.remove("hallen-mode");
  try {
    /** @type {any} */
    const doc = document;
    if (doc.fullscreenElement && typeof doc.exitFullscreen === "function") {
      await doc.exitFullscreen();
    } else if (doc.webkitFullscreenElement && typeof doc.webkitExitFullscreen === "function") {
      doc.webkitExitFullscreen();
    }
  } catch (err) {
    console.warn("[hallen-mode] exit fullscreen failed", err);
  }
  try {
    localStorage.setItem(STORAGE_KEY, "off");
  } catch {
    /* no-op */
  }
}

/** @returns {Promise<void>} */
export async function toggleHallenMode() {
  if (isHallenMode()) {
    await disableHallenMode();
  } else {
    await enableHallenMode();
  }
}

/**
 * Restore persisted state on boot. CSS class only — fullscreen requires
 * a user gesture, so we don't re-enter fullscreen here.
 */
export function initHallenModeFromStorage() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "on") {
      document.documentElement.classList.add("hallen-mode");
    }
  } catch {
    /* no-op */
  }
  // Attach global fullscreenchange listener so ESC/user-exit reconciles state.
  const onChange = () => {
    /** @type {any} */
    const doc = document;
    const inFS = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    if (!inFS && isHallenMode()) {
      document.documentElement.classList.remove("hallen-mode");
      try {
        localStorage.setItem(STORAGE_KEY, "off");
      } catch {
        /* no-op */
      }
    }
  };
  document.addEventListener("fullscreenchange", onChange);
  document.addEventListener("webkitfullscreenchange", onChange);
}
